import { css } from '@emotion/react'
import {
  DesignToken,
  Heading,
  SpacingToken,
  Text,
} from '@livechat/design-system-react-components'
import { addDays, format } from 'date-fns'

import PriceFormatter from '@/components/Checkout/PriceFormatter'
import { DATE_FORMAT } from '@/constants'
import useParamSelector from '@/hooks/useParamSelector'
import { useTypedSelector } from '@/store/hooks'
import {
  getCartAgentsCountForProduct,
  getChargeType,
  getPaymentCharge,
  getPaymentItem,
} from '@/store/selectors'
import { calculateChargePrice } from '@/utils/products'
import { withVar } from '@/utils/styles'

const wrapperCss = css`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  gap: var(${SpacingToken.Spacing05}) var(${SpacingToken.Spacing3});
  overflow: visible;

  h4 {
    margin: 0;
  }

  > div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(${SpacingToken.Spacing1});

    &:last-of-type {
      align-items: flex-end;
      text-align: right;
    }
  }
`

const PaymentSummary = () => {
  const charge = useTypedSelector(getPaymentCharge)
  const chargeType = useTypedSelector(getChargeType)
  const item = useTypedSelector(getPaymentItem)
  const agentsCount = useParamSelector(
    getCartAgentsCountForProduct,
    item?.product
  )

  if (!charge) {
    return null
  }

  const months = charge?.months || 1
  const frequency = chargeType || item?.payment?.frequency
  const trialDays = charge.trial_days || 0
  const trialEndDate = format(
    addDays(new Date(), trialDays),
    DATE_FORMAT.SHORT_DATE
  )

  const { totalPrice, totalPriceTax, taxRate } = calculateChargePrice({
    charge,
    agentsCount,
  })

  return (
    <div css={wrapperCss} data-test="billed-now-wrapper">
      <div>
        <Heading size="sm">
          {trialDays > 0 ? 'Billed after trial' : 'Billed now'}
        </Heading>

        {trialDays > 0 && (
          <Text
            size="xs"
            customColor={withVar(DesignToken.ContentBasicSecondary)}
          >
            Payment for this app will be processed on {trialEndDate}.
          </Text>
        )}

        <Text
          size="xs"
          customColor={withVar(DesignToken.ContentBasicSecondary)}
        >
          We&apos;ll collect the payment from card linked to your account.
        </Text>
      </div>

      <div>
        <Heading size="sm">
          <PriceFormatter
            value={totalPrice}
            frequency={frequency}
            months={months}
          />
        </Heading>

        {!!taxRate && (
          <Text
            size="xs"
            customColor={withVar(DesignToken.ContentBasicSecondary)}
          >
            Includes tax {taxRate}% (<PriceFormatter value={totalPriceTax} />)
          </Text>
        )}
      </div>
    </div>
  )
}

export default PaymentSummary
